// F2/F3 — **THE CAPTURE AND THE READING, ON THE PAGE.**
//
// **IT IS ITS OWN COMPONENT AND IT IS COMPOSED INTO `FunnelPage` AS A NODE**, which is `HomeShowcase`'s
// pattern exactly: a form holds state, and the moment the page held state it would stop being
// CALLABLE — and `FunnelPage` being hookless is the only reason a gate can render all three doors and
// read the tree back. So the state lives here and the page stays a pure function of its props.
//
// Every rule about what a complete submission is lives in `src/funnelCapture.js` and is asked by BOTH
// this form and the server door (R15: a picker that offers what the gate refuses costs the whole
// entry before she is told no — and this is a stranger the owner PAID to bring here, who does not
// try twice).
import { useEffect, useRef, useState } from "react";
import {
  FUNNEL_CONSENT, TIMELESS_NOTE, FUNNEL_FINISH_CTA,
  leadViolations, leadRefusal, leadIsTimeless,
} from "../funnelCapture.js";
import { bridgeHref } from "../funnelCapture.js";
// FUNNEL-FIX-1: her trust copy and her question cards are DECLARATIONS in the funnels roster, so the
// page composes no wording of its own — one safety promise for three doors (R28/R29).
import { FUNNEL_WHY, FUNNEL_SAFETY, funnelQuestionCards, landerReviews } from "../funnels.js";
import { READING_CARDS } from "../homeTapMap.js";
// >>> THE APPROVED POOL IS READ THROUGH THE HOUSE'S ONE READER (R28/R29). <<< `useReviews` already
// carries REV-1's whole privacy law at the door and the R18 split between *the read failed* and
// *the house has none* — a second fetch here would be a second answer to which quotes a stranger
// may see, on the surface where getting it wrong puts a member's private words on an ad landing.
import { ReviewRail, useReviews, type ReviewRow } from "./ReviewRail";
import { searchPlaces, lookupStateFor, keepsResults, type PlaceLookupState } from "../lib/place-search";

// >>> F2's LAST GAP, CLOSED WITH HER PRECISION LAW ON IT (owner, 2026-08-12). <<<
//
// The capture shipped with a FREE-TEXT birthplace and no geocoder, and `funnel-lead-walk.mjs` holds
// the consequence as a PAIRED clause: a clean city resolves and delivers a real reading, and an
// AMBIGUOUS one — measured live, `"Minneapolis, Minnesota"` — returns two Minneapolises, so
// `resolveBirthPlace` correctly declines to guess and **that visitor gets no reading at all.** The
// refusal is right (a wrong birthplace casts a chart that renders perfectly, R19) and the gap is in
// the CAPTURE, which is where this closes it: coordinates are resolved BEFORE submit, so the
// server-side resolver becomes a FALLBACK rather than the only path.
//
// **IT IS THE HOUSE'S OWN PICKER LAW AND NOT A SECOND ONE.** `searchPlaces` / `lookupStateFor` /
// `keepsResults` are the same three functions the birth forms and the astrocartography room resolve
// through, so I1's newest-keystroke abort ruling, R97's `response.ok` reading and R94's `source`
// distinction all apply here by EXISTING rather than by this form remembering them (R28/R29) — and
// the row it lists is named by the precision law, so a stranger from Tarzana is offered Tarzana.
type PlaceRow = {
  place_name: string; state?: string; country?: string; country_code: string;
  latitude: string; longitude: string; timezone_id: string; formatted?: string;
};

// **HER REDESIGN, 2026-08-13 — THIS PAGE NO LONGER HAS A `Reading` TYPE, AND THAT IS THE POINT.**
//
// The full reading generates once, stores against the lead's row, and is **never shown whole and
// never emailed**: it is waiting in her room the moment she joins. What this component receives is
// ONE paragraph that cliffhangs, and a bridge. A `Reading` shape declared here would be an open
// invitation for a later round to render the rest of it, which is the one thing the model forbids.
type Bridge = {
  line?: string; next?: string; cta?: string; token?: string;
  framing?: string; hooks?: string[];
};

// The raw `?f=` as it arrived, nullable exactly as the page takes it — `funnelFromParam` resolves an
// absent or unrostered key on the SERVER, and a client that narrowed the type here would be a second
// place this house decides which door a lead came through (R28/R29).
export function FunnelCapture({ funnel }: { funnel?: string | null }) {
  const [form, setForm] = useState({
    email: "", phone: "", birthDate: "", birthTime: "", birthTimeUnknown: false,
    birthPlace: "", consent: false, honeypot: "",
    // The resolved place travels WHOLE — R118's law, which this house has already paid for once: a
    // patch that moves the coordinates and leaves the last place's zone name behind is worse than one
    // that moves nothing, because `localDayFor` prefers the NAME over the offset. So these are set
    // together and cleared together, and never conditionally.
    //
    // **`timezoneName` AND NOT `timezone`, WHICH IS THE FIELD THAT REFUSED EVERY PICKER SUBMISSION.**
    // The geocoder's `timezone_id` is an IANA NAME; `timezone` is read downstream as a NUMERIC UTC
    // OFFSET, and a name in it built `NaN`, refused the payload and answered `requiresInput` with
    // nothing on screen to say so. The house's own birth form has always kept these in two fields
    // (`resolvedPlacePatch` writes `timezoneName` beside `timezone`) and this one collapsed them.
    // The offset is resolved server-side from these coordinates and the BIRTH DATE, which is the
    // more correct answer anyway: a 1977 birth in Ohio is not today's offset.
    latitude: "", longitude: "", timezoneName: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [teaser, setTeaser] = useState<string>("");
  const [bridge, setBridge] = useState<Bridge | null>(null);
  // D-5's law, one room over: a `useState` guard reads the value from the render that BOUND the
  // handler, so a double tap inside one frame sends twice. A submission that sends twice on a fenced
  // public door burns her throttle against the person it belongs to.
  const inFlight = useRef(false);
  const [placeRows, setPlaceRows] = useState<PlaceRow[]>([]);
  const [placeLookup, setPlaceLookup] = useState<PlaceLookupState>("idle");
  // >>> **LANDER-1: THE POOL IS ASKED FOR ON ARRIVAL NOW, AND THAT IS NOT A REVERSAL OF
  // FUNNEL-FIX-1's SPEED DECISION — IT IS THE HALF THAT MAKES BOTH HER CLAUSES TRUE. <<<**
  //
  // That round deferred it because *"a visitor still filling in the form costs a request that could
  // not draw anything"*, which was right while nothing on the lander drew reviews. Her A3 puts three
  // of them UNDER THE FOLD — so there IS something to draw, and **her own placement is what makes
  // the request free of the speed budget**: nothing above the fold waits on it, the form is
  // interactive before it lands, and a pool that never arrives draws the same nothing an empty one
  // does. The hook still runs unconditionally, because a hook behind an early return is a
  // hook-order crash.
  const reviewRows = useReviews(true);
  // >>> **BOTH FUNNEL SURFACES TAKE HER SHORT BLOCK THROUGH THE ONE DECLARED LAW, AND FF1-5's
  // RENDERED PROOF IS WHAT FOUND THE SECOND ONE DID NOT.** <<< Her FF1-4 clause is *"a SHORT reviews
  // block (the approved pool)"* on the results page and A3's is three on the lander — and this line
  // kept EVERY quote it was given, which measured at coordinates as **42 quotes below her cliffhang
  // on a phone**, the opposite of the compact conversion block she describes. LANDER-1 left it with
  // `scope rule 4` written at this line, which was right for a round about the lander and is what
  // this round's own walk then convicted (R43's M8: a clause counting cards is green over exactly
  // that). One law, both surfaces, so *how many reviews a funnel surface shows* cannot become two
  // answers at two call sites (R28/R29) — and the count is one constant if she wants it moved.
  const reviewQuotes = landerReviews(reviewRows);
  const landerQuotes = reviewQuotes;
  const placeAbort = useRef<AbortController | null>(null);

  const set = (k: string, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  // Typing REPLACES a resolved place, because coordinates resolved against a town she has since
  // edited are not evidence about the town she is typing now — the same reason `keepsResults` drops
  // rows on every answer but an abort.
  const typePlace = (value: string) =>
    setForm((f) => ({ ...f, birthPlace: value, latitude: "", longitude: "", timezoneName: "" }));

  const pickPlace = (row: PlaceRow) => {
    setForm((f) => ({
      ...f,
      birthPlace: row.formatted || [row.place_name, row.state, row.country].filter(Boolean).join(", "),
      latitude: row.latitude,
      longitude: row.longitude,
      // UNCONDITIONALLY, per R118: an empty zone is the honest answer when the geocoder sent none,
      // and it is exactly when a leftover name from the last place would win over the fresh one.
      timezoneName: row.timezone_id || "",
    }));
    setPlaceRows([]);
    setPlaceLookup("idle");
  };

  // Debounced, and aborted on the next keystroke. A public page the owner PAID to bring a stranger
  // to may not fire a geocode per character.
  useEffect(() => {
    const typed = form.birthPlace.trim();
    if (form.latitude || typed.length < 2) { setPlaceRows([]); setPlaceLookup("idle"); return; }
    const timer = setTimeout(async () => {
      placeAbort.current?.abort();
      const controller = new AbortController();
      placeAbort.current = controller;
      setPlaceLookup("searching");
      const answer = await searchPlaces<PlaceRow>(typed, { signal: controller.signal });
      // I1's ruling, held by the shared law rather than restated here: an abort is a NEWER keystroke
      // winning, so the rows and the state are left exactly as they were and the list never flickers.
      if (!keepsResults(answer)) setPlaceRows(answer.results);
      setPlaceLookup((previous) => lookupStateFor(answer, previous));
    }, 350);
    return () => clearTimeout(timer);
  }, [form.birthPlace, form.latitude]);

  async function submit(e: { preventDefault: () => void }) {
    e.preventDefault();
    if (inFlight.current) return;
    const hits = leadViolations(form);
    if (hits.length) { setError(leadRefusal(hits)); return; }
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/funnel/lead", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...form, funnel }),
      });
      const data = await res.json().catch(() => null);
      // R39/R47: READ THE PAYLOAD, never the status code alone. This door answers 200 with
      // `ok: false` for a repeat the retry rail still owes, and a client reading the status would
      // call that a success and render an empty page.
      //
      // **THE PARAGRAPH IS WHAT ARRIVES, AND ITS ABSENCE IS A PENDING LEAD RATHER THAN AN ERROR.**
      // Her details are saved and the retry rail is running, so she reads the warm sentence the
      // server composed rather than a failure she cannot act on.
      if (!data?.ok || !data?.teaser) {
        setError(data?.message || "That did not come through. Try again in a moment.");
        return;
      }
      setTeaser(String(data.teaser));
      setBridge(data.bridge || null);
    } catch {
      setError("That did not come through. Try again in a moment.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  if (teaser) return <FunnelTeaserView teaser={teaser} bridge={bridge} reviews={reviewQuotes} reviewRows={reviewRows} />;

  return (
    <form className="funnel__form" onSubmit={submit} noValidate>
      {/* >>> FUNNEL-FIX-1 clause 1 — THE TWO LINES THAT EARN THE FORM. <<< Her words: a short warm
          explanation of WHY, and the safety line, *"small, classy, gold on the house dark, NOT a
          legal wall, a hand extended."*

          **THEY ARE ABOVE THE FIELDS AND NOT UNDER THE BUTTON**, because a reason to trust the form
          that arrives after she has already decided whether to fill it in is a reason she never
          reads — which is the same finding L8 made about the finish door three blocks below the
          sentence that broke off.

          The words are the roster's; this composes none of its own. */}
      <p className="funnel__why">{FUNNEL_WHY}</p>
      <p className="funnel__safety">{FUNNEL_SAFETY}</p>
      <label className="funnel__field">
        <span>Your email</span>
        <input
          type="email" name="email" autoComplete="email" inputMode="email" required
          value={form.email} onChange={(ev) => set("email", ev.target.value)}
        />
      </label>
      <label className="funnel__field">
        <span>Your birth date</span>
        <input
          type="date" name="birthDate" required
          value={form.birthDate} onChange={(ev) => set("birthDate", ev.target.value)}
        />
      </label>
      {/* HER RULING, 2026-08-13: THE TIME IS REQUIRED, AND `UNKNOWN` IS AN ANSWER RATHER THAN AN
          EMPTY FIELD. The input is disabled and cleared while the box is ticked, so the two controls
          can never disagree about what she said — and `required` comes OFF it in that state, because
          a native validation wall on a field she has just declared unknowable is a dead form. */}
      <label className="funnel__field">
        <span>Your birth time</span>
        <input
          type="time" name="birthTime" required={!form.birthTimeUnknown}
          disabled={form.birthTimeUnknown}
          value={form.birthTimeUnknown ? "" : form.birthTime}
          onChange={(ev) => set("birthTime", ev.target.value)}
        />
      </label>
      <label className="funnel__unknown">
        <input
          type="checkbox" name="birthTimeUnknown"
          checked={form.birthTimeUnknown}
          onChange={(ev) => setForm((f) => ({
            ...f,
            birthTimeUnknown: ev.target.checked,
            // Ticking CLEARS the time. The empty string is the timeless law's own carrier all the way
            // to the row, so an unticked-then-reticked visitor cannot leave a stale minute behind
            // that the cast would then use (R118's whole-patch law, one control down).
            birthTime: ev.target.checked ? "" : f.birthTime,
          }))}
        />
        <span>I do not know my birth time</span>
      </label>
      {/* The timeless law, said BEFORE she submits rather than after — a stranger who ticks the box
          should know exactly what she is getting, not discover it in the reading. */}
      {leadIsTimeless(form) ? <p className="funnel__note">{TIMELESS_NOTE}</p> : null}
      <label className="funnel__field funnel__field--place">
        <span>Where you were born</span>
        <input
          type="text" name="birthPlace" autoComplete="off"
          value={form.birthPlace} onChange={(ev) => typePlace(ev.target.value)}
        />
        {/* The rows carry their FULL label, so two places of the same name are told apart by the
            visitor rather than guessed at by us — which is the whole reason the ambiguous case
            refused on the server before this existed. */}
        {placeRows.length ? (
          <ul className="funnel__places">
            {placeRows.map((row) => (
              <li key={`${row.place_name}-${row.latitude}-${row.longitude}`}>
                <button type="button" onClick={() => pickPlace(row)}>
                  {row.formatted || [row.place_name, row.state, row.country].filter(Boolean).join(", ")}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {/* THREE FACTS, THREE SENTENCES (R18/R97): still asking, asked and knows no such place, and
            could not be asked at all. Collapsing them is how a member is told this house does not
            know her town over a lookup that never answered. A resolved place says so plainly, because
            silence after a tap reads as the tap not having landed. */}
        {form.latitude ? <p className="funnel__note">Found it — casting on this exact spot.</p>
          : placeLookup === "searching" ? <p className="funnel__note">Looking…</p>
          : placeLookup === "none" ? <p className="funnel__note">No match yet — we will still try from what you typed.</p>
          : placeLookup === "unavailable" ? <p className="funnel__note">Place lookup is quiet right now — we will still try from what you typed.</p>
          : null}
      </label>
      <label className="funnel__field">
        <span>Phone <em>(optional)</em></span>
        <input
          type="tel" name="phone" autoComplete="tel" inputMode="tel"
          value={form.phone} onChange={(ev) => set("phone", ev.target.value)}
        />
      </label>
      {/* F6's cheapest fence: a field no human can see. It is hidden by the STYLESHEET rather than by
          `type="hidden"`, because a hidden input is not something a form filler bothers with and a
          styled one is exactly what it walks into. `tabIndex={-1}` and `aria-hidden` keep it away
          from a keyboard and a screen reader, so no real person can reach it by accident. */}
      <input
        className="funnel__hp" type="text" name="company" tabIndex={-1} aria-hidden="true"
        autoComplete="off" value={form.honeypot} onChange={(ev) => set("honeypot", ev.target.value)}
      />
      <label className="funnel__consent">
        <input
          type="checkbox" name="consent"
          checked={form.consent} onChange={(ev) => set("consent", ev.target.checked)}
        />
        <span>{FUNNEL_CONSENT}</span>
      </label>
      {error ? <p className="funnel__error" role="alert">{error}</p> : null}
      <button className="funnel__go" type="submit" disabled={busy}>
        {busy ? "Reading your chart…" : "Read my chart"}
      </button>
      {/* >>> **A3's THREE APPROVED REVIEWS, UNDER THE FOLD — AND UNDER THE BUTTON, WHICH IS THE
          SAME THING ON A PHONE.** <<< Her sentence puts them BELOW her one action, so nothing here
          stands between a visitor and the thing she came to do, and nothing above the fold waits on
          the read. **An empty or unarrived pool draws NOTHING** (T9-2, and her own ruling that a
          stranger is never told something broke). The law owns the count; this composes none of it. */}
      {landerQuotes.length ? (
        <ul className="funnel__reviews funnel__reviews--lander">
          {landerQuotes.map((r, i) => <li key={i} className="funnel__review">{r}</li>)}
        </ul>
      ) : null}
    </form>
  );
}

/**
 * ================================================================================================
 * **THE ONE PARAGRAPH, AND THE FINISH DOOR UNDER IT (her redesign, 2026-08-13).**
 *
 * `FunnelReadingView` is **DELETED at its declaration rather than left standing beside this**
 * (DL-2/DT-2b/R181). It rendered the whole reading — the kicker, every section, the cards — and the
 * one thing this model exists to prevent is the full reading reaching a stranger's screen. A retired
 * renderer sitting in the same file is one import away from doing exactly that, and the next reader
 * cannot tell which one the page wears.
 *
 * **THERE IS NOTHING HERE THAT COULD RENDER A READING.** This component's whole input is a STRING and
 * a bridge; it has no sections to walk and no cards to list, so *"the reading is never shown whole"*
 * is a property of the component rather than a rule somebody remembers (R18 as a shape, matching the
 * two columns the row keeps it in).
 *
 * Hookless, so a gate CALLS it and reads the tree back.
 * ================================================================================================
 */
export function FunnelTeaserView(
  { teaser, bridge, reviews = [], reviewRows = null }: { teaser: string; bridge?: Bridge | null; reviews?: string[]; reviewRows?: ReviewRow[] | null },
) {
  // >>> THE CARDS ARE DERIVED HERE RATHER THAN PASSED IN, BECAUSE THEY ARE A FACT ABOUT THE HOUSE
  // AND NOT ABOUT THIS VISITOR. <<< The REVIEWS are a prop: they come off her approved pool, which
  // is a live read, and a component that fetched its own would be a second reader of REV-1's
  // ledger (R28/R29).
  const cards = funnelQuestionCards(READING_CARDS);
  const line = bridge?.line || "";
  const framing = bridge?.framing || "";
  const hooks = bridge?.hooks || [];
  return (
    <div className="funnel__reading">
      {/* T9-2's no-body-no-box law: an absent paragraph draws no box over nothing. The caller never
          reaches this view without one, and a view that cannot be handed nothing is a view whose
          empty state was decided by luck. */}
      {teaser ? <p className="funnel__teaser">{teaser}</p> : null}
      {/* >>> **L8 (her live-test order, 2026-08-13) — THE DOOR SITS DIRECTLY AT THE INTERRUPTION
          POINT.** <<< Her words: *"the paragraph CUTS MID-REVELATION, at the moment they need the
          next sentence, and 'Finish this reading' sits DIRECTLY at the interruption point."* It used
          to sit at the bottom of the bridge box, three blocks of copy below the sentence that broke
          off — so the one moment a reader most wants the rest of it was answered by a hook line, a
          framing sentence and a bullet list before anything she could tap. **It is ONE door and it
          MOVED**, rather than a second one added beside the old one: two buttons offering the same
          act is a page asking twice. */}
      {teaser && framing ? (
        <a className="funnel__bridge-go funnel__bridge-go--cut" href={bridgeHref(bridge?.token || "")}>
          {bridge?.cta || FUNNEL_FINISH_CTA}
        </a>
      ) : null}
      {/* HER FINISH DOOR. The hook line, the framing sentence naming what THIS funnel decodes, the
          other hooks, and the button that carries her to the HOME PAGE with the tier popup ready —
          carrying the signed handoff token, which is what makes her continuity law a property of the
          LINK rather than of anything this client remembers.

          **THE BUTTON IS DRAWN ON THE FRAMING RATHER THAN ON THE HOOK LINE.** The framing is what
          names the act (`funnelFinishFraming`), and a paragraph that cliffhangs with no way onward is
          the one screen this whole redesign exists to avoid — so the door is conditional on the
          sentence that is composed for every rostered funnel, never on the optional flourish above
          it. */}
      {framing ? (
        <div className="funnel__bridge">
          {line ? <p className="funnel__bridge-line">{line}</p> : null}
          <p className="funnel__bridge-next">{framing}</p>
          {/* L9: the other hooks, in Title Case (the CASE is in the roster, because Title Case is
              not a transform a stylesheet can perform) and LEFT-ALIGNED with their own bullets — her
              complaint was centred text hanging off left bullets, which is a `text-align` on the box
              this list sits in rather than anything about the list. */}
          {hooks.length ? (
            <ul className="funnel__hooks">
              {hooks.map((h) => <li key={h}>{h}</li>)}
            </ul>
          ) : null}
          {/* L8: the button is NOT drawn again here. It moved UP to the interruption point, and a
              second copy at the foot of this box would be the same act offered twice. */}
        </div>
      ) : null}

      {/* >>> FUNNEL-FIX-1 clause 4 — THE OTHER READINGS, AS DOORS. <<< Her ruling: *"EVERYTHING
          TAPPABLE: the dead teaser text that clicks to nothing becomes real doors or dies."*

          **THE HOOK LIST ABOVE IS WHAT SHE IS POINTING AT** and it is left exactly as it is — it is
          the OTHER HOOKS for THIS funnel's own bridge, which is L9's copy and hers. What was missing
          is anything to tap; these are that, and they are a different roster on purpose: the hooks
          tease what this reading did not finish, and these are readings a member can go and get.

          **EACH LANDS ON SIGNUP WITH THE CONTINUITY TOKEN** — the same `bridgeHref` the finish door
          uses, never a second transport (R28/R29) — so L10's pre-fill law rides every one of them by
          EXISTING rather than by this surface remembering it.

          The order is her order: the cards, then the reviews, then the safety line small. On a phone
          this whole block simply follows the finish button, which is her compact version — one
          document order, and the stylesheet does the rest, so there is no second markup tree to keep
          in step. */}
      {teaser ? (
        <div className="funnel__more">
          {cards.length ? (
            <ul className="funnel__cards">
              {cards.map((c) => (
                <li key={c.reading}>
                  <a className="funnel__card" href={bridgeHref(bridge?.token || "")}>{c.question}</a>
                </li>
              ))}
            </ul>
          ) : null}
          {/* T9-2's no-body-no-box law: an empty approved pool draws no heading over nothing. */}
          {/* >>> HER ITEM 5: THE REVIEW CAROUSEL REACHES EVERY FUNNEL RESULTS PAGE. <<< It was a
              static list here while her /talk capture carried the scrolling strip, so the same
              approved pool wore two different faces on two conversion surfaces. `place="talk"` is
              that strip -- reusing it rather than styling a second one means REV-1's privacy law,
              the reduced-motion ruling, the seamless double-draw and the silent-failure law all
              reach this page by EXISTING (R28/R29). One results view serves all three funnels, so
              this is every one of them.

              The string quotes stay as the FALLBACK for a pool the rail cannot draw, because a
              results page with a gap where the reviews were is worse than a plain list. */}
          {reviewRows && reviewRows.length ? (
            <ReviewRail rows={reviewRows} place="talk" />
          ) : reviews.length ? (
            <ul className="funnel__reviews">
              {reviews.map((r, i) => <li key={i} className="funnel__review">{r}</li>)}
            </ul>
          ) : null}
          <p className="funnel__safety funnel__safety--small">{FUNNEL_SAFETY}</p>
        </div>
      ) : null}
    </div>
  );
}
